import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import LanguageSwitcher from '../components/LanguageSwitcher';
import MapComponent from '../components/GoogleMapComponent';
import CreditManagement from './CreditManagement';
import { 
  Car, Users, DollarSign, TrendingUp, 
  LayoutDashboard, UserCircle, MapPin, 
  LogOut, Search, Star, Wallet
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === 'ar';
  
  const [currentView, setCurrentView] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [rides, setRides] = useState([]);
  
  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      setLoading(true);
      
      if (currentView === 'dashboard') {
        const response = await axios.get(`${API_BASE}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
        
        // Also fetch recent rides for dashboard
        const ridesResponse = await axios.get(`${API_BASE}/api/admin/rides`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRides(ridesResponse.data.slice(0, 5));
      } else if (currentView === 'drivers') {
        const response = await axios.get(`${API_BASE}/api/admin/drivers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDrivers(response.data);
      } else if (currentView === 'passengers') {
        const response = await axios.get(`${API_BASE}/api/admin/passengers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPassengers(response.data);
      } else if (currentView === 'rides') {
        const response = await axios.get(`${API_BASE}/api/admin/rides`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRides(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
      toast({
        title: isArabic ? 'خطأ' : 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [currentView, navigate, isArabic]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const renderDashboard = () => {
    if (loading || !stats) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6 bg-gradient-to-br from-black to-black text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-black text-sm mb-1">{t('totalUsers')}</p>
                <p className="text-3xl font-bold">{stats.totalUsers.toLocaleString()}</p>
              </div>
              <Users className="h-12 w-12 text-black" />
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm mb-1">{t('activeDrivers')}</p>
                <p className="text-3xl font-bold">{stats.activeDrivers}</p>
              </div>
              <Car className="h-12 w-12 text-green-200" />
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-black to-black text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-black text-sm mb-1">{t('activeRides')}</p>
                <p className="text-3xl font-bold">{stats.activeRides}</p>
              </div>
              <MapPin className="h-12 w-12 text-black" />
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm mb-1">{t('revenue')} ({isArabic ? 'اليوم' : 'Today'})</p>
                <p className="text-3xl font-bold">{(stats.todayRevenue / 1000).toFixed(1)}K</p>
              </div>
              <DollarSign className="h-12 w-12 text-orange-200" />
            </div>
          </Card>
        </div>
        
        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{isArabic ? 'إيرادات اليوم' : "Today's Revenue"}</h3>
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.todayRevenue.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-1">{isArabic ? 'ل.س' : 'SYP'}</p>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{isArabic ? 'إيرادات الأسبوع' : "Week Revenue"}</h3>
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.weekRevenue.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-1">{isArabic ? 'ل.س' : 'SYP'}</p>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{isArabic ? 'إيرادات الشهر' : "Month Revenue"}</h3>
              <TrendingUp className="h-5 w-5 text-black" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.monthRevenue.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-1">{isArabic ? 'ل.س' : 'SYP'}</p>
          </Card>
        </div>
        
        {/* Recent Activity */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">{isArabic ? 'النشاط الأخير' : 'Recent Activity'}</h3>
          <div className="space-y-3">
            {rides.map((ride) => (
              <div key={ride.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-gray-100 p-2 rounded-lg">
                    <Car className="h-5 w-5 text-black" />
                  </div>
                  <div>
                    <p className="font-medium">{ride.passengerName} → {ride.driverName || 'Waiting...'}</p>
                    <p className="text-sm text-gray-600">{ride.pickup.address}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600">{ride.fare.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}</p>
                  <Badge variant={ride.status === 'completed' ? 'success' : 'default'} className="mt-1">
                    {t(ride.status)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };
  
  const renderDrivers = () => {
    if (loading) {
      return <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>;
    }
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{t('drivers')}</h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder={isArabic ? 'بحث...' : 'Search...'}
                className="pl-10 w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
        
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isArabic ? 'السائق' : 'Driver'}</TableHead>
                <TableHead>{isArabic ? 'السيارة' : 'Vehicle'}</TableHead>
                <TableHead>{t('rating')}</TableHead>
                <TableHead>{t('totalRides')}</TableHead>
                <TableHead>{t('earnings')}</TableHead>
                <TableHead>{t('status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drivers.filter(d => 
                d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                d.email.toLowerCase().includes(searchQuery.toLowerCase())
              ).map((driver) => (
                <TableRow key={driver.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={`https://ui-avatars.com/api/?name=${driver.name}&background=4f46e5&color=fff`} />
                        <AvatarFallback>{(driver.name || 'D').charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{driver.name}</p>
                        <p className="text-sm text-gray-600">{driver.phone}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{driver.carModel}</p>
                    <p className="text-sm text-gray-600">{driver.carNumber}</p>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium">{driver.rating}</span>
                    </div>
                  </TableCell>
                  <TableCell>{driver.totalRides}</TableCell>
                  <TableCell>{driver.earnings.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}</TableCell>
                  <TableCell>
                    <Badge className={driver.status === 'online' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {driver.status === 'online' ? t('online') : t('offline')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    );
  };
  
  const renderPassengers = () => {
    if (loading) {
      return <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>;
    }
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{t('passengers')}</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder={isArabic ? 'بحث...' : 'Search...'}
              className="pl-10 w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isArabic ? 'الراكب' : 'Passenger'}</TableHead>
                <TableHead>{t('rating')}</TableHead>
                <TableHead>{t('totalRides')}</TableHead>
                <TableHead>{isArabic ? 'تاريخ الانضمام' : 'Join Date'}</TableHead>
                <TableHead>{t('status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {passengers.filter(p => 
                p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.email.toLowerCase().includes(searchQuery.toLowerCase())
              ).map((passenger) => (
                <TableRow key={passenger.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={`https://ui-avatars.com/api/?name=${passenger.name}&background=10b981&color=fff`} />
                        <AvatarFallback>{passenger.name[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{passenger.name}</p>
                        <p className="text-sm text-gray-600">{passenger.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium">{passenger.rating}</span>
                    </div>
                  </TableCell>
                  <TableCell>{passenger.totalRides}</TableCell>
                  <TableCell>{new Date(passenger.joinDate).toLocaleDateString(isArabic ? 'ar-SY' : 'en-US')}</TableCell>
                  <TableCell>
                    <Badge className="bg-green-100 text-green-800">
                      {passenger.status === 'active' ? (isArabic ? 'نشط' : 'Active') : (isArabic ? 'معلق' : 'Inactive')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    );
  };
  
  const renderRides = () => {
    if (loading) {
      return <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>;
    }
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{t('rides')}</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder={isArabic ? 'بحث...' : 'Search...'}
              className="pl-10 w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isArabic ? 'معرف الرحلة' : 'Ride ID'}</TableHead>
                <TableHead>{isArabic ? 'الراكب' : 'Passenger'}</TableHead>
                <TableHead>{isArabic ? 'السائق' : 'Driver'}</TableHead>
                <TableHead>{isArabic ? 'المسافة' : 'Distance'}</TableHead>
                <TableHead>{isArabic ? 'الأجرة' : 'Fare'}</TableHead>
                <TableHead>{t('status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rides.map((ride) => (
                <TableRow key={ride.id}>
                  <TableCell className="font-mono text-sm">{ride.id.slice(0, 8)}</TableCell>
                  <TableCell>{ride.passengerName}</TableCell>
                  <TableCell>{ride.driverName || '-'}</TableCell>
                  <TableCell>{ride.distance}</TableCell>
                  <TableCell className="font-semibold">{ride.fare.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}</TableCell>
                  <TableCell>
                    <Badge variant={ride.status === 'completed' ? 'success' : 'default'}>
                      {t(ride.status)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/saeed-logo.png" alt="Saeed" className="h-10 w-10 object-contain bg-black rounded-lg p-1" />
            <h1 className="text-xl font-bold tracking-wide">SAEED — {t('adminPanel')}</h1>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={() => {
              localStorage.removeItem('token');
              navigate('/');
            }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r min-h-screen p-4">
          <nav className="space-y-2">
            <Button 
              variant={currentView === 'dashboard' ? 'secondary' : 'ghost'} 
              className="w-full justify-start"
              onClick={() => setCurrentView('dashboard')}
            >
              <LayoutDashboard className="mr-2 h-4 w-4" />
              {t('dashboard')}
            </Button>
            
            <Button 
              variant={currentView === 'credits' ? 'secondary' : 'ghost'} 
              className="w-full justify-start"
              onClick={() => setCurrentView('credits')}
            >
              <Wallet className="mr-2 h-4 w-4" />
              {isArabic ? 'إدارة الرصيد' : 'Credit Management'}
            </Button>
            
            <Button 
              variant={currentView === 'drivers' ? 'secondary' : 'ghost'} 
              className="w-full justify-start"
              onClick={() => setCurrentView('drivers')}
            >
              <Car className="mr-2 h-4 w-4" />
              {t('drivers')}
            </Button>
            
            <Button 
              variant={currentView === 'passengers' ? 'secondary' : 'ghost'} 
              className="w-full justify-start"
              onClick={() => setCurrentView('passengers')}
            >
              <UserCircle className="mr-2 h-4 w-4" />
              {t('passengers')}
            </Button>
            
            <Button 
              variant={currentView === 'rides' ? 'secondary' : 'ghost'} 
              className="w-full justify-start"
              onClick={() => setCurrentView('rides')}
            >
              <MapPin className="mr-2 h-4 w-4" />
              {t('rides')}
            </Button>
          </nav>
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            {currentView === 'dashboard' && renderDashboard()}
            {currentView === 'credits' && <CreditManagement />}
            {currentView === 'drivers' && renderDrivers()}
            {currentView === 'passengers' && renderPassengers()}
            {currentView === 'rides' && renderRides()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
